import React, { useState, useEffect } from 'react';
import { Add_document, List_document, Update_document, Delete_document, List_country } from '../../Utils/Api';
import { toast } from 'react-toastify';

const Documents = () => {
    const [documents, setDocuments] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        countryId: '',
        status: 0,
    });
    const [editingId, setEditingId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    useEffect(() => {
        fetchDocuments();
        fetchCountries();
    }, []);

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const response = await List_document();
            if (response && response.data) {
                setDocuments(response.data);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
            toast.error('Failed to fetch documents');
        } finally {
            setLoading(false);
        }
    };

    const fetchCountries = async () => {
        try {
            const response = await List_country();
            if (response && response.data?.data) {
                setCountries(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            toast.error('Please enter document title');
            return;
        }
        if (!formData.countryId) {
            toast.error('Please select a country');
            return;
        }

        setLoading(true);
        try {

            if (editingId) {
                await Update_document(editingId, formData);
                toast.success('Document updated successfully');
            } else {
                await Add_document(formData);
                toast.success('Document added successfully');
            }

            resetForm();
            setShowModal(false);
            fetchDocuments();
        } catch (error) {
            console.error('Error saving document:', error);
            toast.error('Failed to save document');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            countryId: '',
            status: 0,
        });
        setEditingId(null);
    };

    const handleEdit = (document) => {
        setFormData({
            title: document.title || '',
            description: document.description || '',
            countryId: document.countryId?._id || document.countryId || '',
            status: document.status?.toString() || 0,
        });
        setEditingId(document._id || document.id);

        setShowModal(true);
    };

    const handleDelete = async () => {
        if (!deleteId) return;

        setLoading(true);
        try {
            await Delete_document(deleteId);
            toast.success('Document deleted successfully');
            setDeleteId(null);
            fetchDocuments();
        } catch (error) {
            console.error('Error deleting document:', error);
            toast.error('Failed to delete document');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        resetForm();
        setShowModal(true);
    };

    return (
        <div className="main-content ">
            <div className="row my-4 mx-1">
                <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
                    <h1 className="page-title fw-semibold fs-18 mb-0 mt-2">Documents</h1>
                    <div className="ms-md-1 ms-0 ">
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item"><a href="#">Management</a></li>
                                <li className="breadcrumb-item active" aria-current="page">Documents</li>
                            </ol>
                        </nav>
                    </div>
                </div>

                {/* Main Content Card */}
                <div className="card custom-card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="card-title mb-0">Documents List</h6>
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={handleAdd}
                        >
                            <i className="ri-add-line me-1"></i> Add Document
                        </button>
                    </div>
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table text-nowrap">
                                <thead className="table-primary">
                                    <tr>
                                        <th scope="col">#</th>
                                        <th scope="col">Title</th>
                                        <th scope="col">Description</th>
                                        <th scope="col">Country</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    {!loading && documents.length === 0 && (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4">
                                                No documents found
                                            </td>
                                        </tr>
                                    )}

                                    {!loading && documents.map((document, index) => (
                                        <tr key={document._id || document.id}>
                                            <th scope="row">{index + 1}</th>
                                            <td>{document.title}</td>
                                            <td>
                                                {document.description ? (
                                                    <span title={document.description}>
                                                        {document.description.length > 50
                                                            ? `${document.description.substring(0, 50)}...`
                                                            : document.description}
                                                    </span>
                                                ) : (
                                                    'N/A'
                                                )}
                                            </td>
                                            <td>{document.countryId?.name || 'N/A'}</td>
                                            <td>
                                                <span className={`badge ${document.status === 0 ? 'bg-success-transparent' : 'bg-danger-transparent'}`}>
                                                    {document.status === 0 ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="hstack gap-2 fs-15">
                                                    <button
                                                        onClick={() => handleEdit(document)}
                                                        className="btn btn-icon btn-sm btn-info-transparent rounded-pill"
                                                        title="Edit"
                                                    >
                                                        <i className="ri-edit-line"></i>
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteId(document._id || document.id)}
                                                        className="btn btn-icon btn-sm btn-danger-transparent rounded-pill"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#deleteModal"
                                                        title="Delete"
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Add/Edit Modal */}
                {showModal && (
                    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        {editingId ? 'Edit Document' : 'Add New Document'}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowModal(false)}
                                    ></button>
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="modal-body">
                                        <div className="row">
                                            {/* Document Title */}
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="title" className="form-label">Document Title <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="title"
                                                    name="title"
                                                    value={formData.title}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter document title"
                                                    required
                                                />
                                            </div>

                                            {/* Country */}
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="countryId" className="form-label">Country <span className="text-danger">*</span></label>
                                                <select
                                                    className="form-select"
                                                    id="countryId"
                                                    name="countryId"
                                                    value={formData.countryId}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select a country</option>
                                                    {countries.map(country => (
                                                        <option key={country._id} value={country._id}>
                                                            {country.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Description */}
                                            <div className="col-12 mb-3">
                                                <label htmlFor="description" className="form-label">Description</label>
                                                <textarea
                                                    className="form-control"
                                                    id="description"
                                                    name="description"
                                                    value={formData.description}
                                                    onChange={handleInputChange}
                                                    rows="3"
                                                    placeholder="Enter document description"
                                                ></textarea>
                                            </div>

                                            {/* Status */}
                                            <div className="col-md-6 mb-3 d-none">
                                                <label htmlFor="status" className="form-label">Status</label>
                                                <select
                                                    className="form-select"
                                                    id="status"
                                                    name="status"
                                                    value={formData.status}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="0">Active</option>
                                                    <option value="1">Inactive</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => setShowModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    {editingId ? 'Updating...' : 'Adding...'}
                                                </>
                                            ) : (
                                                editingId ? 'Update' : 'Add'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                <div className="modal fade" id="deleteModal" tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirm Delete</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                    onClick={() => setDeleteId(null)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this document?</p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    data-bs-dismiss="modal"
                                    onClick={() => setDeleteId(null)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    data-bs-dismiss="modal"
                                    onClick={handleDelete}
                                    disabled={loading}
                                >
                                    {loading ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Documents;